import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, UserCog, EyeOff, UserPlus, X, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ROLES } from '@/lib/constants';

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  // v167.2 — delete-user confirmation modal state. Holding the FULL
  // row so we can render the email + role + tenant inside the dialog.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  // v167.3 — Restore tab: tab selector + list of soft-deleted users.
  // Lives in a separate array so the active table never has to filter.
  const [tab, setTab] = useState('active');           // 'active' | 'deleted'
  const [deletedItems, setDeletedItems] = useState([]);
  const [restoring, setRestoring] = useState(null);   // user_id currently being restored

  const loadDeleted = useCallback(async () => {
    try {
      const { data } = await api.get('/users/deleted?days=30');
      setDeletedItems(data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load deleted users');
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/users/');
      setItems(data);
    } catch (err) {
      console.error('UsersAdmin: load failed', err);
      toast.error('Could not load users');
    }
  }, []);
  // CRA's eslint-config-react-app doesn't have the 'set-state-in-effect'
  // rule registered, but our project lint tool does. Use a generic disable
  // that both pipelines tolerate.
  // eslint-disable-next-line
  useEffect(() => { void load(); }, []);

  const updateRole = async (user_id, role) => {
    try {
      await api.patch('/users/role', { user_id, role });
      toast.success('Role updated.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (user_id, is_active) => {
    try {
      await api.patch('/users/active', { user_id, is_active });
      toast.success(is_active ? 'Activated.' : 'Deactivated.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  /**
   * Soft-delete a user. Tombstones the record on the server — the
   * email is rotated to `@deleted.local`, is_active flips false, and
   * a `deleted_at` audit field is stamped. The listing API filters
   * tombstones out automatically.
   */
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.user_id}`, {
        data: deleteReason.trim() ? { reason: deleteReason.trim() } : undefined,
      });
      toast.success(`Deleted ${deleteTarget.email}.`);
      setDeleteTarget(null);
      setDeleteReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const restoreUser = async (row) => {
    if (!window.confirm(
      `Restore ${row.original_email || row.email}? They'll regain access immediately.`
    )) return;
    setRestoring(row.user_id);
    try {
      await api.post(`/users/${row.user_id}/restore`);
      toast.success(`Restored ${row.original_email || row.email}.`);
      // Refresh both tabs so the user moves out of "deleted" into "active".
      await loadDeleted();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  // When the Maker switches to the Deleted tab for the first time,
  // fetch the tombstone list. Subsequent switches reuse the cache
  // until restoreUser() invalidates it.
  useEffect(() => {
    // eslint-disable-next-line
    if (tab === 'deleted') void loadDeleted();
    // eslint-disable-next-line
  }, [tab]);

  /**
   * Maker-only: replace the current session token with a JWT that
   * authenticates as the target user. The page reloads onto whatever
   * dashboard that user normally lands on (persona router handles it).
   */
  const cloakAs = async (user_id, email) => {
    if (!window.confirm(`Cloak as ${email}? Your Maker session is preserved on the server — drop the cloak from the bottom-left banner to return.`)) return;
    try {
      const { data } = await api.post(`/maker/cloak/${user_id}`);
      // Backend swaps the session cookie for the cloak token. Mirror
      // it into the in-memory bearer too in case any pending axios call
      // fires before the hard reload picks up the new cookie.
      const { setToken } = await import('@/lib/tokenStore');
      setToken(data.access_token || data.token);
      toast.success(`Now cloaked as ${email}.`);
      // Hard reload so all stale tenant/role state resets.
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cloak failed.');
    }
  };

  const isMaker = me?.is_maker;
  const isCoMaker = me?.is_co_maker;
  // Tenant admins (holding_admin) can create users inside their own
  // tenant only — same permission check the backend enforces.
  const canCreate = isMaker || me?.role === 'system_admin' || me?.role === 'holding_admin';

  /**
   * Maker/Co-Maker tier guard — mirrors backend `maker_tier.require_can_modify`.
   * Returns a string when the row is locked for the current viewer
   * (used as tooltip + disabled marker), or `null` when freely mutable.
   */
  const tierLockReason = (row) => {
    if (row.is_maker) return 'The Maker account is immutable from this table.';
    if (row.is_co_maker && !isMaker) return 'Only the Maker can modify a Co-Maker.';
    return null;
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="ve-eyebrow">User Management</div>
          <h1 className="display text-3xl md:text-4xl mt-2">Granular roles, real control.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Every user, every role, every permission — auditable in one place.</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowCreate(true)}
            data-testid="users-create-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#8B3FC5] text-white text-sm font-semibold hover:bg-[#AE52EA] transition-colors shadow-[0_4px_20px_-4px_rgba(139,63,197,0.5)]"
          >
            <UserPlus size={15} />
            Create user
          </button>
        )}
      </div>

      {/* v167.3 — Active / Deleted tabs */}
      <div className="mt-6 flex items-center gap-2" data-testid="users-tabs">
        <button
          type="button"
          onClick={() => setTab('active')}
          data-testid="users-tab-active"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-bold border transition-colors ${
            tab === 'active'
              ? 'bg-[#8B3FC5] text-white border-[#7932AE]'
              : 'bg-white text-slate-700 border-slate-300 hover:border-violet-400'
          }`}
        >
          <UserCog size={12} /> Active <span className="font-mono opacity-80">{items.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setTab('deleted')}
          data-testid="users-tab-deleted"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-bold border transition-colors ${
            tab === 'deleted'
              ? 'bg-slate-700 text-white border-slate-800'
              : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
          }`}
          title="Soft-deleted users from the last 30 days — restoreable"
        >
          <Trash2 size={12} /> Deleted (30d) <span className="font-mono opacity-80">{deletedItems.length}</span>
        </button>
      </div>

      {tab === 'active' && (
      <div className="mt-4 ve-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.15em] text-xs">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-left px-5 py-3">Tenant</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map(u => {
              // Highlight Maker + Co-Maker rows so admins know the tier.
              // Source of truth is the backend flag, not role inference.
              const isMakerRow   = !!u.is_maker;
              const isCoMakerRow = !!u.is_co_maker;
              const lockReason   = tierLockReason(u);
              const locked       = !!lockReason;
              const rowBg = isMakerRow
                ? 'bg-[#FFF8E5]'
                : (isCoMakerRow ? 'bg-violet-50/50' : '');
              return (
                <tr
                  key={u.user_id}
                  data-testid={`users-row-${u.user_id}`}
                  className={`hover:bg-slate-50 ${rowBg}`}
                >
                  <td className="px-5 py-3">
                    <div className="font-medium flex items-center gap-1.5">
                      {u.name}
                      {isMakerRow && (
                        <span
                          data-testid={`users-pill-maker-${u.user_id}`}
                          className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#8B3FC5] text-white font-bold"
                        >
                          Maker
                        </span>
                      )}
                      {isCoMakerRow && (
                        <span
                          data-testid={`users-pill-co-maker-${u.user_id}`}
                          className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border border-violet-400 text-violet-700 font-bold bg-white"
                        >
                          Co-Maker
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mono">{u.user_id}</div>
                  </td>
                  <td className="px-5 py-3">{u.email}</td>
                  <td className="px-5 py-3">
                    {locked ? (
                      <span
                        title={lockReason}
                        data-testid={`users-role-locked-${u.user_id}`}
                        className="text-xs text-slate-500 italic"
                      >
                        — {isMakerRow ? 'immutable' : 'maker-only'} —
                      </span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={e => updateRole(u.user_id, e.target.value)}
                        data-testid={`users-role-select-${u.user_id}`}
                        className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[#8B3FC5]"
                      >
                        {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 mono">{u.tenant_id || 'primary'}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded ${u.is_active === false ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'}`}>
                      {u.is_active === false ? 'inactive' : 'active'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {!locked && (
                        <button
                          onClick={() => toggleActive(u.user_id, !(u.is_active !== false))}
                          className="ve-btn-ghost text-xs"
                          data-testid={`users-toggle-${u.user_id}`}
                        >
                          {u.is_active === false ? 'Activate' : 'Deactivate'}
                        </button>
                      )}
                      {isMaker && !isMakerRow && (
                        <button
                          onClick={() => cloakAs(u.user_id, u.email)}
                          data-testid={`users-cloak-${u.user_id}`}
                          title="Maker-only: log in as this user (audit-invisible)"
                          className="px-2 py-1 text-[11px] rounded-md border border-violet-300 text-violet-700 hover:bg-violet-50 flex items-center gap-1"
                        >
                          <EyeOff size={11} /> Cloak
                        </button>
                      )}
                      {!locked && u.user_id !== me?.user_id && (
                        <button
                          onClick={() => { setDeleteTarget(u); setDeleteReason(''); }}
                          data-testid={`users-delete-${u.user_id}`}
                          title="Soft-delete this user — preserves audit trail"
                          className="px-2 py-1 text-[11px] rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 flex items-center gap-1"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      )}
                      {locked && (
                        <span
                          data-testid={`users-locked-${u.user_id}`}
                          title={lockReason}
                          className="px-2 py-1 text-[10px] uppercase tracking-wider rounded-md border border-slate-200 text-slate-500 bg-slate-50 cursor-help"
                        >
                          {isMakerRow ? 'Maker-locked' : 'Maker-only'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {tab === 'deleted' && (
        <div className="mt-4 ve-card overflow-hidden" data-testid="users-deleted-panel">
          {deletedItems.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              <Trash2 size={28} className="mx-auto mb-2 opacity-40" />
              <div className="font-medium">No soft-deleted users in the last 30 days.</div>
              <div className="text-xs mt-1">When you delete a user, they land here for 30 days, restoreable.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.15em] text-xs">
                <tr>
                  <th className="text-left px-5 py-3">Original email</th>
                  <th className="text-left px-5 py-3">Role</th>
                  <th className="text-left px-5 py-3">Tenant</th>
                  <th className="text-left px-5 py-3">Deleted</th>
                  <th className="text-left px-5 py-3">By</th>
                  <th className="text-left px-5 py-3">Reason</th>
                  <th className="text-left px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deletedItems.map((u) => {
                  const ts = u.deleted_at ? new Date(u.deleted_at).toLocaleString() : '—';
                  const canRestore = !!u.original_email;
                  return (
                    <tr
                      key={u.user_id}
                      data-testid={`users-deleted-row-${u.user_id}`}
                      className="hover:bg-slate-50"
                    >
                      <td className="px-5 py-3 font-mono text-[12px] text-slate-900 truncate" title={u.original_email}>
                        {u.original_email || <span className="italic text-slate-400">(no original recorded)</span>}
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold">
                          {u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 mono">{u.tenant_id || 'primary'}</span>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600">{ts}</td>
                      <td className="px-5 py-3 text-xs text-slate-600 truncate" title={u.deleted_by_email}>
                        {u.deleted_by_email || '—'}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600 italic max-w-[220px] truncate" title={u.deleted_reason}>
                        {u.deleted_reason || '—'}
                      </td>
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          disabled={!canRestore || restoring === u.user_id}
                          onClick={() => restoreUser(u)}
                          data-testid={`users-restore-${u.user_id}`}
                          title={canRestore ? 'Restore this user' : 'Cannot auto-restore — original email not recorded'}
                          className="px-2 py-1 text-[11px] rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {restoring === u.user_id ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <ShieldCheck size={11} />
                          )}
                          Restore
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="mt-6 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
        <ShieldCheck size={14} className="text-[#8B3FC5]"/>
        <span>All actions are audit-logged. <UserCog size={12} className="inline mx-1"/> Total users: <strong className="text-slate-900">{items.length}</strong></span>
        {isMaker && <span className="ml-3 text-[#5E1F8B]">· Maker actions are cloaked from audit.</span>}
        {isCoMaker && (
          <span
            data-testid="users-co-maker-notice"
            className="ml-3 text-violet-700"
          >
            · You are signed in as a Co-Maker. You can manage everyone except the Maker.
          </span>
        )}
      </div>

      {showCreate && (
        <CreateUserModal
          me={me}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !deleting && setDeleteTarget(null)}
          data-testid="users-delete-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-zinc-900 border border-rose-500/40 rounded-xl w-full max-w-md shadow-2xl"
          >
            <div className="px-5 py-4 border-b border-slate-200 dark:border-white/10 flex items-center gap-2">
              <Trash2 size={18} className="text-rose-500" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Delete user?</h2>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-md bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 px-3 py-2.5 text-xs">
                <div className="font-mono text-slate-900 dark:text-white truncate">{deleteTarget.email}</div>
                <div className="text-slate-500 dark:text-white/55 mt-0.5">
                  {deleteTarget.role} · {deleteTarget.tenant_id || 'primary'}
                </div>
              </div>
              <p className="text-xs text-slate-700 dark:text-white/75 leading-relaxed">
                The user will be soft-deleted — they can no longer sign in, the
                email is released (a future signup with the same address will
                trigger a fresh access-request), and the audit trail is preserved.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
                  Reason <span className="opacity-60 normal-case font-normal">(optional, for audit log)</span>
                </label>
                <textarea
                  rows={2}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Left the company, mistake, security review…"
                  data-testid="users-delete-reason"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-zinc-950/40">
              <button
                type="button"
                onClick={() => !deleting && setDeleteTarget(null)}
                disabled={deleting}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 dark:text-white/80 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="users-delete-confirm"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete user
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * UMS create-user modal — gathers email/name/role/tenant/company,
 * POSTs to /api/users/create, fires a magic-link welcome email
 * automatically (backend side-effect), and refreshes the parent table.
 *
 * Tenant field is only shown for the platform Maker (system_admin) —
 * tenant admins are locked to their own tenant by the backend, so the
 * field is hidden to avoid confusion.
 */
function CreateUserModal({ me, onClose, onCreated }) {
  const isSys = me?.is_maker || me?.role === 'system_admin';
  const [email, setEmail]     = useState('');
  const [name, setName]       = useState('');
  const [role, setRole]       = useState('founder');
  const [tenantId, setTenant] = useState(me?.tenant_id || 'primary');
  const [company, setCompany] = useState('');
  const [busy, setBusy]       = useState(false);

  // Tenant admins cannot mint system_admins — filter the role dropdown.
  const ASSIGNABLE = isSys ? ROLES : ROLES.filter(r => r.id !== 'system_admin');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        company: company.trim() || null,
      };
      if (isSys && tenantId.trim()) body.tenant_id = tenantId.trim();
      const { data } = await api.post('/users/create', body);
      toast.success(`User created · ${data.email}`, {
        description: 'Welcome email with sign-in link is on its way.',
      });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create user.');
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="users-create-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-2xl p-6 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="ve-eyebrow text-[#8B3FC5]">Create user</div>
            <h3 className="text-lg font-semibold mt-1">Provision an account</h3>
            <p className="text-xs text-slate-500 mt-1">
              The new user will receive a welcome email with a one-tap
              sign-in link. No password is set or asked.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="users-create-close"
            className="text-slate-400 hover:text-slate-900 shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
          Email
          <input
            type="email" required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="users-create-email"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm normal-case tracking-normal focus:border-[#8B3FC5] focus:outline-none"
            placeholder="founder@startup.com"
          />
        </label>

        <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
          Full name
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="users-create-name"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm normal-case tracking-normal focus:border-[#8B3FC5] focus:outline-none"
            placeholder="Jane Doe"
          />
        </label>

        <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="users-create-role"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#8B3FC5] focus:outline-none normal-case tracking-normal"
          >
            {ASSIGNABLE.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </label>

        {isSys && (
          <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
            Tenant
            <input
              required
              value={tenantId}
              onChange={(e) => setTenant(e.target.value)}
              data-testid="users-create-tenant"
              className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#8B3FC5] focus:outline-none normal-case tracking-normal font-mono"
              placeholder="primary"
            />
            <span className="block mt-1 text-[10px] text-slate-400 normal-case tracking-normal font-normal">
              Use the tenant&apos;s slug. Defaults to <code>primary</code>. As Maker
              you can create users in any tenant.
            </span>
          </label>
        )}

        <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
          Company (optional)
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            data-testid="users-create-company"
            className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#8B3FC5] focus:outline-none normal-case tracking-normal"
            placeholder="Acme Inc."
          />
        </label>

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="users-create-cancel"
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="users-create-submit"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#8B3FC5] text-white text-sm font-semibold hover:bg-[#AE52EA] disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create & send welcome email'}
          </button>
        </div>
      </form>
    </div>
  );
}
